import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Clock, MessageSquare } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur, errMsg } from "@/lib/download";
import { Questionnaire } from "@/components/pkg/Questionnaire";
import { ChatSheet } from "@/components/pkg/ChatSheet";
import { UnreadDot } from "@/components/pkg/UnreadDot";
import { useUnread } from "@/lib/useUnread";
import { itDate } from "@/lib/download";

const prefill = (it, inp) => {
  const d = inp.dateTo && inp.dateTo !== inp.date ? `dal ${itDate(inp.date)} al ${itDate(inp.dateTo)}` : `il ${itDate(inp.date)}`;
  return `Buongiorno ${it.name}, sto organizzando un evento (${inp.eventType}) a ${inp.place} ${d} e il portale ti ha proposto per "${it.macro}" in un pacchetto Evento Chiavi in Mano. Prima di confermare vorrei chiederti: `;
};

const CLIENTS = ["public_entity", "business", "citizen"];

const Proposal = ({ p, i, onOption, onContact, unread, busy }) => (
  <div className="card-premium p-6 flex flex-col" data-testid={`proposal-${i}`}>
    <div className="flex items-center justify-between"><h3 className="font-display text-2xl font-bold text-aic-navy">{p.title}</h3>{i === 0 && <span className="chip-gold">Consigliata</span>}</div>
    <p className="text-sm text-slate-600 mt-2">{p.description}</p>
    <ul className="mt-4 space-y-2 flex-1">
      {p.items.map((it) => (
        <li key={it.providerId} className="flex items-center gap-3 text-sm">
          <img src={mediaUrl(it.photo) || "/logo-icon.png"} alt="" className="w-10 h-10 rounded-full object-cover border border-slate-200"/>
          <div className="flex-1 min-w-0"><Link to={`/artista/${it.providerId}`} target="_blank" className="font-semibold text-aic-navy hover:text-[#C98A3C] truncate block">{it.name}</Link>
            <div className="text-xs text-slate-500 truncate">{it.macro} · {it.city} · {it.distanceKm} km{it.days > 1 ? ` · ${it.days} giorni` : ""}{it.packageAvailable ? " · aderisce ai pacchetti" : ""}</div></div>
          <div className="text-right text-xs"><b className="text-sm">{eur(it.net)}</b>{it.travel > 0 && <div className="text-slate-400">di cui trasferta {eur(it.travel)}</div>}
            <button type="button" onClick={() => onContact(it)} data-testid={`proposal-${i}-contact-${it.providerId}`} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#C98A3C] hover:underline"><MessageSquare className="w-3.5 h-3.5"/>Contatta<UnreadDot n={unread[it.providerId]} testId={`proposal-${i}-unread-${it.providerId}`}/></button></div>
        </li>
      ))}
    </ul>
    <div className="mt-5 pt-4 border-t border-slate-100 text-sm space-y-1">
      <div className="flex justify-between"><span>Imponibile (commissioni incluse)</span><span>{eur(p.net)}</span></div>
      <div className="flex justify-between text-slate-500"><span>IVA 22%</span><span>{eur(p.vat)}</span></div>
      <div className="flex justify-between font-display text-xl font-bold text-aic-navy" data-testid={`proposal-total-${i}`}><span>Totale</span><span>{eur(p.total)}</span></div>
    </div>
    <button disabled={busy} onClick={() => onOption(i)} data-testid={`proposal-option-${i}`} className="btn-pill-gold justify-center mt-4"><Clock className="w-4 h-4"/>Opziona gratis per 24 ore</button>
  </div>
);

export default function ChiaviInMano() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chat, setChat] = useState(null);
  const [unread, refreshUnread] = useUnread(!!res);
  const contact = (it) => setChat({ id: it.providerId, name: it.name, macro: it.macro, note: "le proposte restano aperte qui sotto", prefill: prefill(it, res.input) });
  const generate = async (body) => {
    setBusy(true);
    try { setRes((await api.post("/packages/generate", body)).data); setTimeout(() => document.querySelector('[data-testid="pkg-results"]')?.scrollIntoView({ behavior: "smooth" }), 100); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const option = async (index) => {
    setBusy(true);
    try { await api.post("/packages/option", { generationId: res.id, index }); toast.success("Pacchetto opzionato per 24 ore: gli artisti sono stati avvisati"); nav("/pacchetti"); } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <div className="bg-white">
      <div className="bg-aic-navy text-white"><div className="max-w-7xl mx-auto px-6 py-14">
        <span className="overline text-gold flex items-center gap-2"><KeyRound className="w-4 h-4"/>Evento Chiavi in Mano</span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mt-2">Il tuo evento completo, in un solo pacchetto</h1>
        <p className="text-white/80 mt-3 max-w-2xl">Rispondi a poche domande guidate: componiamo per te pacchetti con artisti e fornitori liberi in quelle date e in quella zona. Puoi opzionarli gratuitamente per 24 ore.</p>
      </div></div>
      <div className="max-w-4xl mx-auto px-6 py-10">
        {!user ? <div className="card-premium p-8 text-center" data-testid="pkg-login-required"><p className="mb-4">Accedi o iscriviti gratuitamente per comporre un pacchetto Evento Chiavi in Mano.</p><div className="flex gap-3 justify-center"><Link to="/login" className="btn-pill-outline">Accedi</Link><Link to="/registrati" className="btn-pill-gold">Iscriviti</Link></div></div>
          : !CLIENTS.includes(user.role) ? <div className="card-premium p-8 text-center" data-testid="pkg-not-client">I pacchetti sono riservati ai committenti (Enti, Imprese, Privati). Puoi aderire ai pacchetti dalla tua <Link to="/dashboard" className="underline font-semibold">Dashboard</Link>.</div>
          : <Questionnaire onSubmit={generate} busy={busy}/>}
      </div>
      <div className="max-w-7xl mx-auto px-6 pb-10">
        {res && <div data-testid="pkg-results">
          {res.missing?.length > 0 && <p className="mb-4 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3" data-testid="pkg-missing">Nessuno disponibile in quella data e zona per: {res.missing.join(", ")}.</p>}
          {res.proposals.length === 0 ? <p className="text-slate-500" data-testid="pkg-none">Nessun pacchetto possibile: prova altre date o modifica le risposte dal riepilogo.</p>
            : <div className="grid lg:grid-cols-3 gap-6">{res.proposals.map((p, i) => <Proposal key={i} p={p} i={i} onOption={option} onContact={contact} unread={unread} busy={busy}/>)}</div>}
        </div>}
        <ChatSheet target={chat} onClose={() => { setChat(null); refreshUnread(); }}/>
      </div>
    </div>
  );
}
